import React, { useEffect, useRef } from 'react';
import useSeo from '../lib/seo';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { PROJECTS } from '../data/portfolio';
import PageHero from '../components/site/PageHero';
import { finePointer } from '../components/home/v3/motion';

gsap.registerPlugin(ScrollTrigger, useGSAP);

function CaseCard({ p, wide }) {
  const ref = useRef(null);

  // Luz que segue o cursor pela borda do card (spotlight), sem estado React.
  useEffect(() => {
    const el = ref.current;
    if (!el || !finePointer()) return undefined;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${e.clientX - r.left}px`);
      el.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    el.addEventListener('pointermove', move);
    return () => el.removeEventListener('pointermove', move);
  }, []);

  return (
    <Link
      to={p.path}
      ref={ref}
      className={`wk-case ${wide ? 'is-wide' : ''}`}
      style={{ '--accent': p.accent }}
      aria-label={`${p.name}: ${p.tagline}`}
    >
      <span className="wk-case-light" aria-hidden />
      <div className="wk-case-copy">
        <div className="wk-case-top">
          <h2 className="wk-case-name">{p.name}</h2>
          <span className="wk-case-go" aria-hidden>
            <ArrowUpRight size={22} strokeWidth={1.75} />
          </span>
        </div>
        <p className="wk-case-tagline">{p.tagline}</p>
        <p className="wk-case-desc">{p.desc}</p>
        <ul className="wk-case-tags" aria-label="Plataformas">
          {p.platforms.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
      <div className="wk-case-media" aria-hidden>
        <div className="wk-case-glow" />
        <div className="hv3-phone wk-phone">
          <div
            className={`hv3-phone-screen ${p.screenTop ? 'has-inset' : ''}`}
            style={p.screenTop ? { '--screen-top': p.screenTop } : undefined}
          >
            <img src={p.screen} alt="" width="600" height="1300" loading="lazy" decoding="async" />
          </div>
          <span className="hv3-phone-island" />
        </div>
      </div>
    </Link>
  );
}

function NossoTrabalho() {
  const root = useRef(null);

  useSeo({
    title: 'Nosso Trabalho | Idealizer',
    description: 'PsicoID, Revyou, EG Treinos e Meu Atalho: apps e plataformas que a Idealizer desenhou, desenvolveu e mantém no ar.',
    path: '/nosso-trabalho',
  });

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap.utils.toArray('.wk-case', root.current).forEach((card) => {
          gsap.from(card, {
            y: 80,
            autoAlpha: 0,
            duration: 1.2,
            ease: 'expo.out',
            scrollTrigger: { trigger: card, start: 'top 88%' },
          });
          // O celular sobe dentro do card enquanto ele atravessa a tela.
          gsap.fromTo(
            card.querySelector('.wk-phone'),
            { yPercent: 18 },
            { yPercent: -6, ease: 'none', scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: true } }
          );
        });
      });
      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <main className="pg work-v3" ref={root}>
      <PageHero
        lines={['Produtos reais.', { text: 'No ar agora.', accent: true }]}
        lede="Cada projeto saiu de uma ideia e hoje está publicado, nas mãos de usuários reais. Do design ao backend, feitos aqui."
      />

      <section className="container wk-grid" aria-label="Projetos">
        {PROJECTS.map((p, i) => (
          <CaseCard p={p} key={p.slug} wide={i === 0 || i === 3} />
        ))}
      </section>

      <section className="container pg-cta">
        <div className="cta-card">
          <div className="cta-glow" aria-hidden />
          <h2 className="cta-title">O próximo pode ser o seu.</h2>
          <p className="cta-sub">
            Conte o que você quer construir. Em poucas perguntas a gente entende a
            ideia e volta com um plano para tirar do papel.
          </p>
          <div className="cta-actions">
            <Link to="/contato" className="btn-primary">
              Começar um projeto <ArrowRight size={18} strokeWidth={2} aria-hidden />
            </Link>
            <Link to="/sobre" className="btn-ghost">
              Conhecer a Idealizer
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default NossoTrabalho;
